import React, { memo, useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import { isImageCached, markImageLoaded, resolveFullImageUrl } from "../../utils/imageCache";
import "./ProfileAvatar.css";

/**
 * ProfileAvatar — Standardized Profile Avatar Component
 * Ensures exact 1:1 circular ratio, anti-distortion object-cover,
 * and persistent session-cached image loading without re-render blinks.
 */
function ProfileAvatar({
  src,
  size = 40,
  variant = "gradient",
  isOnline = false,
  link,
  alt = "Profile",
  className = "",
  style = {},
  onClick,
}) {
  // Resolve image source with CDN fallback
  const resolvedSrc = useMemo(() => {
    if (!src || src === Avatar) return Avatar;
    if (typeof src !== "string") return Avatar;
    const trimmed = src.trim();
    if (!trimmed || trimmed === "undefined" || trimmed === "null") return Avatar;
    return resolveFullImageUrl(trimmed) || Avatar;
  }, [src]);

  const isLocalPlaceholder = resolvedSrc === Avatar || resolvedSrc.includes("placeholder_Awatar");
  const isAlreadyCached = isLocalPlaceholder || isImageCached(resolvedSrc);
  const [isLoaded, setIsLoaded] = useState(() => isAlreadyCached);
  const [imgSrc, setImgSrc] = useState(resolvedSrc);
  const imgRef = useRef(null);

  useEffect(() => {
    setImgSrc(resolvedSrc);
    if (isLocalPlaceholder || isImageCached(resolvedSrc)) {
      setIsLoaded(true);
    } else if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      markImageLoaded(resolvedSrc);
      setIsLoaded(true);
    } else {
      setIsLoaded(false);
    }
  }, [resolvedSrc, isLocalPlaceholder]);

  const numericSize = typeof size === "number" ? size : parseInt(size, 10) || 40;
  const sizeStyle = {
    width: `${numericSize}px`,
    height: `${numericSize}px`,
    minWidth: `${numericSize}px`,
    minHeight: `${numericSize}px`,
  };

  const variantClass = `mr-profile-avatar-wrap--${variant}`;

  const avatarContent = (
    <div
      className={`mr-profile-avatar-wrap ${variantClass} ${className}`}
      style={{ ...sizeStyle, ...style }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="mr-profile-avatar-inner">
        {!isLoaded && (
          <div className="mr-avatar-shimmer" aria-hidden="true">
            {numericSize >= 32 && <div className="mr-avatar-shimmer-spinner" />}
          </div>
        )}
        <img
          ref={imgRef}
          src={imgSrc}
          alt={alt}
          className="rounded-circle h-100 w-100 object-cover"
          style={{
            width: "100%",
            height: "100%",
            aspectRatio: "1 / 1",
            objectFit: "cover",
            borderRadius: "50%",
            display: "block",
            opacity: isLoaded ? 1 : 0,
            transition: isAlreadyCached ? "none" : "opacity 0.2s ease-in-out",
          }}
          onLoad={() => {
            markImageLoaded(resolvedSrc);
            setIsLoaded(true);
          }}
          onError={() => {
            if (imgSrc !== Avatar && !imgSrc.includes("placeholder_Awatar")) {
              setImgSrc(Avatar);
            }
            setIsLoaded(true);
          }}
          loading="eager"
          decoding="async"
        />
      </div>
      {isOnline && <span className="mr-avatar-status-dot" title="Online" />}
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="mr-profile-avatar-root" onClick={onClick}>
        {avatarContent}
      </Link>
    );
  }

  return avatarContent;
}

export default memo(ProfileAvatar);

